"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import MarketingNav from "~/app/components/marketing-nav";
import MarketingFooter from "~/app/components/marketing-footer";
import { SiteGlueHomeHero } from "~/components/siteglue/home/home-hero";
import { HomeHeroTwoColumn } from "~/components/siteglue/home/home-hero-two-column";
import { isSiteGlueLandingRedesignEnabled } from "~/lib/siteglue/landing-redesign/flag";
import { SiteGlueHomeEmployeesAtWork } from "~/components/siteglue/home/home-employees-at-work";
import { SiteGlueHomeBuiltForHumans } from "~/components/siteglue/home/home-built-for-humans";
import { SiteGlueHomeMeetTrish } from "~/components/siteglue/home/home-meet-trish";
import { SiteGlueHomeHirePath } from "~/components/siteglue/home/home-hire-path";
import { SiteGlueHomeBasePlan } from "~/components/siteglue/home/home-base-plan";
import { SiteGlueHomeHitl } from "~/components/siteglue/home/home-hitl";
import { SiteGlueHomeChannels } from "~/components/siteglue/home/home-channels";
import { SiteGlueHomeImpactReport } from "~/components/siteglue/home/home-impact-report";
import { SiteGlueHomeIntegrations } from "~/components/siteglue/home/home-integrations";
import { SiteGlueHomeDemos } from "~/components/siteglue/home/home-demos";
import { SiteGlueHomePricing } from "~/components/siteglue/home/home-pricing";
import { SiteGlueHomeFaq } from "~/components/siteglue/home/home-faq";
import { SiteGlueHomeFinalCta } from "~/components/siteglue/home/home-final-cta";
import { HowItWorksSteps } from "~/components/siteglue/how-it-works/how-it-works-steps";
import { HomeOptimizationServices } from "~/components/siteglue/home/home-optimization-services";
import { SiteGlueHomeAgentPreview } from "~/components/siteglue/home/home-agent-preview";
import { SiteGlueHomeDirectoryTeaser } from "~/components/siteglue/home/home-directory-teaser";
import { scrollToInboundSection } from "~/lib/inbound-gpt/paths";
import { MarketingAttributionCapture } from "~/components/siteglue/marketing-attribution-capture";

/** Previous configurator homepage. Restored when the redesign flag is off. */
function SiteGlueHomePageLegacy() {
  const router = useRouter();

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const legacyFeatureSections = ["identity", "skills", "integrations", "demo", "agents"];
    if (legacyFeatureSections.includes(hash)) {
      const section = hash === "demo" ? "agents" : hash;
      router.replace(`/features#${section}`);
      return;
    }
    const timer = window.setTimeout(() => scrollToInboundSection(hash), 150);
    return () => window.clearTimeout(timer);
  }, [router]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MarketingAttributionCapture />
      <MarketingNav brandId="siteglue" />
      <main>
        <SiteGlueHomeHero />
        <SiteGlueHomeEmployeesAtWork />
        <SiteGlueHomeBuiltForHumans />
        <SiteGlueHomeMeetTrish />
        <SiteGlueHomeHirePath />
        <SiteGlueHomeHitl />
        <SiteGlueHomeChannels />
        <SiteGlueHomeImpactReport />
        <SiteGlueHomeIntegrations />
        <SiteGlueHomeDemos />
        <SiteGlueHomePricing />
        <SiteGlueHomeFaq />
        <SiteGlueHomeFinalCta />
      </main>
      <MarketingFooter isLanding brandId="siteglue" />
    </div>
  );
}

/** Replit-style SiteGlue homepage (PRD Section 9). */
function SiteGlueHomePageRedesign() {
  const router = useRouter();

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const timer = window.setTimeout(() => scrollToInboundSection(hash), 150);
    return () => window.clearTimeout(timer);
  }, [router]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MarketingAttributionCapture />
      <MarketingNav brandId="siteglue" />
      <main>
        <HomeHeroTwoColumn />
        <HomeOptimizationServices />
        <SiteGlueHomeBasePlan />
        <HowItWorksSteps variant="teaser" />
        <SiteGlueHomeAgentPreview />
        <SiteGlueHomeDirectoryTeaser />
        <SiteGlueHomePricing />
        <SiteGlueHomeFaq />
        <SiteGlueHomeFinalCta variant="url" />
      </main>
      <MarketingFooter isLanding brandId="siteglue" />
    </div>
  );
}

export function SiteGlueHomePage() {
  if (!isSiteGlueLandingRedesignEnabled()) {
    return <SiteGlueHomePageLegacy />;
  }
  return <SiteGlueHomePageRedesign />;
}
